import {getAliasHeader} from './constants';
import {sendSmsApiUrl, threeSixtyApiUrl, vismaApiUrl} from './services';
import {ThreeSixtyApiError} from './ThreeSixtyApiError';
import {ApiRequestOptions, ChangeDueDateRequest, ChangeDueDateResponse, SendSmsRequest} from './types';

export {ThreeSixtyApiError};

export const threeSixtyVismaApiCall = async <T>(
	{endpoint, method = 'GET', body, headers}: ApiRequestOptions
): Promise<T> => {
	try {
		const response = await fetch(`${vismaApiUrl}${endpoint}`, {
			method,
			headers: {
				...getAliasHeader(),
				...headers,
			},
			...(body ? {body: JSON.stringify(body)} : {}),
		});
  
		if (response.ok) {
			return await response.json();
		} else {
			const errorResponse = await response.json();
			throw new Error(
				`Error: ${response.status} - ${errorResponse.message || 'Unknown error'}`
			);
		}
	} catch (err) {
		console.error(`ThreeSixty API Error [${method} ${endpoint}]:`, err);
		throw err;
	}
};

// Throws ThreeSixtyApiError on an error status, and the fetch error on network failure
export const threeSixtyApiCall = async <T = void>(
	{endpoint, method = 'POST', body, headers}: ApiRequestOptions
): Promise<T> => {
	try {
		const response = await fetch(`${threeSixtyApiUrl}${endpoint}`, {
			method,
			headers: {
				...getAliasHeader(),
				...headers,
			},
			...(body ? {body: JSON.stringify(body)} : {}),
		});

		if (response.ok) {
			// If the response has a body, parse it as JSON
			if (response.status !== 204) {
				const json = await response.json();
				return json as T;
			}
			// For 204 No Content, return undefined (void)
			return undefined as T;
		}
		// The error body is not always JSON (e.g. a 502 from the proxy)
		const errorResponse = await response.json().catch(() => ({}));
		const message = typeof errorResponse?.message === 'string' ? errorResponse.message : '';
		throw new ThreeSixtyApiError(response.status, `Error: ${response.status} - ${message || 'Unknown error'}`);
	} catch (err) {
		console.error(`ThreeSixty API Error [${method} ${endpoint}]:`, err);
		throw err;
	}
};

// ChangeDueDateRequest
export async function vismaChangeDueDate(
	requestBody: ChangeDueDateRequest): Promise<ChangeDueDateResponse> {
	try {
		const response = await fetch(`${vismaApiUrl}invoice/duedate`, {
			headers: getAliasHeader(),
			method: 'PUT',
			body: JSON.stringify(requestBody),
		});
  
		if (response.ok) {       
			return await response.json();
		} else {
			const errorResponse = await response.json();
			throw new Error(
				`Error: ${response.status} - ${errorResponse.message || 'Unknown error'}`
			);
		}
	} catch (err) {
		console.error('vismaChangeDueDate Error:', err);
		throw err;
	}
};

export async function sendSmsCall<T>(options: {
    body: SendSmsRequest;
}): Promise<T> {
	const response = await fetch(`${sendSmsApiUrl}`, {
		method: 'POST',
		headers: getAliasHeader(),
		body: JSON.stringify(options.body),
	});

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}

	return response.json() as Promise<T>;
}

// Innsalg (Planleggeren). The API key (pl_..., older keys u360_...) must never reach the browser: on localhost the webpack dev server
// proxies /aiplanner/ to aiplanner staging and adds the key, elsewhere the threesixty backend proxies it
const isInnsalgDevProxy = window.location.hostname === 'localhost';
const innsalgApiUrl = isInnsalgDevProxy ? '/aiplanner/' : `${threeSixtyApiUrl}aiplanner/upsell/`;

// The dev proxy uses Planleggeren's own paths, the backend has its own routes for the same calls
export const INNSALG_ENDPOINTS = {
	lookup: (orgnr: string, kundebehandler: string): string =>
		`${isInnsalgDevProxy ? 'innsalg/' : ''}${orgnr}?kundebehandler=${encodeURIComponent(kundebehandler)}`,
	answer: (visningId: string): string =>
		isInnsalgDevProxy ? `innsalg/${encodeURIComponent(visningId)}/svar` : `${encodeURIComponent(visningId)}/answer`,
};

// Carries Planleggeren's own {error} message (norwegian), which the doc says to show to the agent
export class InnsalgApiError extends Error {
	status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = 'InnsalgApiError';
		this.status = status;
	}
}

// Throws InnsalgApiError on an error status, and the fetch error on timeout/network failure
export const innsalgApiCall = async <T>(
	{endpoint, method = 'GET', body}: ApiRequestOptions
): Promise<T> => {
	const response = await fetch(`${innsalgApiUrl}${endpoint}`, {
		method,
		headers: isInnsalgDevProxy ? {'Content-Type': 'application/json'} : getAliasHeader(),
		// Planleggeren may use the language model on first lookup, the doc recommends 10s
		signal: AbortSignal.timeout(10000),
		...(body ? {body: JSON.stringify(body)} : {}),
	});
	if (!response.ok) {
		const errorResponse = await response.json().catch(() => ({}));
		const message = typeof errorResponse?.error === 'string' ? errorResponse.error : '';
		console.error(`Innsalg API Error [${method} ${endpoint}]: ${response.status} - ${message || 'Unknown error'}`);
		throw new InnsalgApiError(response.status, message);
	}
	return await response.json() as T;
};
